import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber, formatPercent } from '../../styles/tokens'
import { Button, Input, Select } from '../../components/FormControls'
import { EmptyState, ErrorBanner, SuccessBanner, SkeletonRows } from '../../components/Feedback'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'
import TakeoffCanvas from './TakeoffCanvas'
import { countOpenReviews, buildCaptureReviewWarning, OPEN_REVIEW_STATUSES } from './captureReviewWarning'
import { calculateVolume } from '../../lib/quantityEngine'

const MEASUREMENT_TYPES = [
  { value: 'length', label: 'Length' },
  { value: 'area', label: 'Area' },
  { value: 'volume', label: 'Volume (Area × Depth)' },
  { value: 'count', label: 'Count' },
]

// =======================================================================
// TOP-LEVEL PAGE - owns the single Canvas instance and the currently
// selected Drawing/Sheet/Takeoff Item. This replaces the previous
// per-BOQ-item collapsible-row model, which duplicated the canvas and
// made the workspace feel like a CRUD list instead of a single
// professional takeoff surface.
// =======================================================================
export default function TakeoffPage() {
  const { tenderId, revisionId } = useParams()

  const [navCollapsed, setNavCollapsed] = useState(false)
  // Package 3+8 — Workspace foundation. rightSidebarCollapsed mirrors
  // navCollapsed's existing pattern exactly. fullCanvasMode is a pure
  // OVERLAY, never mutating either base preference — effective
  // collapse state is computed (leftCollapsed/rightCollapsed below),
  // so exiting Full Canvas always restores the user's original
  // sidebar choices automatically, with zero extra bookkeeping.
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState(false)
  const [fullCanvasMode, setFullCanvasMode] = useState(false)
  const leftCollapsed = navCollapsed || fullCanvasMode
  const rightCollapsed = rightSidebarCollapsed || fullCanvasMode
  // Visibility state (Package 3): isolateActive is also an OVERLAY —
  // it never mutates hiddenIds/hiddenLayers, so Exit Isolate restores
  // the exact prior visibility with no special-case restoration logic.
  const [isolateActive, setIsolateActive] = useState(false)
  const [lockedIds, setLockedIds] = useState(new Set())
  const [opacityMap, setOpacityMap] = useState(new Map())
  const [selectedSheet, setSelectedSheet] = useState(null)
  const [selectedTakeoffItemId, setSelectedTakeoffItemId] = useState(null)
  const [selectedMeasurementId, setSelectedMeasurementId] = useState(null)
  // PHASE 2: selectedMeasurementId is preserved UNCHANGED as the Primary
  // Selection — every existing consumer (ProvenancePanel, vertex
  // editing, manual value correction) keeps reading this scalar and
  // needs zero modification. selectedMeasurementIds is additive: an
  // ORDERED array (not a Set) where the LAST element is always the
  // most recently added — this makes "Primary = most recently added
  // remaining" a trivial array[length-1] lookup. Invariant enforced by
  // every mutation in applySelectionEvent below: selectedMeasurementId
  // === null iff selectedMeasurementIds.length === 0, and
  // selectedMeasurementId is always a member of the array.
  const [selectedMeasurementIds, setSelectedMeasurementIds] = useState([])
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)' }}>
      <PageHeader tenderId={tenderId} revisionId={revisionId} selectedSheet={selectedSheet} />

      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      <div style={{ display: 'flex', flex: 1, minHeight: 0, gap: 12 }}>
        <div style={{ flexShrink: 0, position: 'relative', width: leftCollapsed ? 36 : 280, transition: 'width 0.15s ease' }}>
          <button
            onClick={() => setNavCollapsed((c) => !c)}
            disabled={fullCanvasMode}
            title={leftCollapsed ? 'Expand navigator' : 'Collapse navigator'}
            style={{ position: 'absolute', top: 6, right: leftCollapsed ? 4 : 6, zIndex: 2, width: 24, height: 24, borderRadius: 6, border: `1px solid ${colors.border}`, background: '#fff', cursor: fullCanvasMode ? 'not-allowed' : 'pointer', fontSize: 12, opacity: fullCanvasMode ? 0.4 : 1 }}
          >
            {leftCollapsed ? '»' : '«'}
          </button>
          {!leftCollapsed && (
            <DrawingSheetNavigator
              tenderId={tenderId}
              onSelectSheet={setSelectedSheet}
              selectedSheetId={selectedSheet?.id}
              onError={setError}
              onSuccess={setSuccessMsg}
            />
          )}
        </div>

        <CanvasWorkspace
          tenderId={tenderId}
          revisionId={revisionId}
          selectedSheet={selectedSheet}
          selectedTakeoffItemId={selectedTakeoffItemId}
          setSelectedTakeoffItemId={setSelectedTakeoffItemId}
          selectedMeasurementId={selectedMeasurementId}
          selectedMeasurementIds={selectedMeasurementIds}
          setSelectedMeasurementId={setSelectedMeasurementId}
          setSelectedMeasurementIds={setSelectedMeasurementIds}
          onError={setError}
          onSuccess={setSuccessMsg}
          rightCollapsed={rightCollapsed}
          rightSidebarCollapsed={rightSidebarCollapsed}
          setRightSidebarCollapsed={setRightSidebarCollapsed}
          fullCanvasMode={fullCanvasMode}
          setFullCanvasMode={setFullCanvasMode}
          isolateActive={isolateActive}
          setIsolateActive={setIsolateActive}
          lockedIds={lockedIds}
          setLockedIds={setLockedIds}
          opacityMap={opacityMap}
          setOpacityMap={setOpacityMap}
        />
      </div>
    </div>
  )
}

function PageHeader({ tenderId, revisionId, selectedSheet }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <EstimationBreadcrumb />
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>
            {selectedSheet ? `${selectedSheet.drawingName || 'Drawing'} - ${selectedSheet.sheetNumber}` : 'Takeoff'}
          </h1>
          {selectedSheet?.versionLabel && (
            <span style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, background: '#F3F4F6', padding: '2px 8px', borderRadius: 999 }}>
              Rev {selectedSheet.versionLabel}
            </span>
          )}
          {selectedSheet?.isOutdated && (
            <span style={{ fontSize: 12, fontWeight: 600, color: colors.warningText, background: colors.warningBg, padding: '2px 8px', borderRadius: 999 }}>
              Newer revision available
            </span>
          )}
        </div>
        <Link
          to={`/estimation/tender/${tenderId}/revision/${revisionId}/takeoff-report`}
          style={{ fontSize: 13, fontWeight: 600, color: colors.primary, textDecoration: 'none', border: `1px solid ${colors.primary}`, borderRadius: 6, padding: '5px 12px', whiteSpace: 'nowrap' }}
        >
          Takeoff Report
        </Link>
      </div>
    </div>
  )
}

// =======================================================================
// LEFT: Drawing / Sheet navigator - collapsible, browse-only.
// =======================================================================
function DrawingSheetNavigator({ tenderId, onSelectSheet, selectedSheetId, onError, onSuccess }) {
  const [drawings, setDrawings] = useState(null)
  const [versions, setVersions] = useState({})
  const [sheets, setSheets] = useState({})
  const [expandedDrawing, setExpandedDrawing] = useState(null)
  const [expandedVersion, setExpandedVersion] = useState(null)
  const [newDrawingName, setNewDrawingName] = useState('')
  const [newVersionLabel, setNewVersionLabel] = useState('')
  const [newSheetNumber, setNewSheetNumber] = useState('')

  const loadDrawings = useCallback(async () => {
    const { data, error } = await supabase.from('drawing').select('id, name').eq('tender_id', tenderId).order('created_at')
    if (error) onError(error.message)
    else setDrawings(data)
  }, [tenderId, onError])

  useEffect(() => { loadDrawings() }, [loadDrawings])

  async function loadVersions(drawingId) {
    const { data, error } = await supabase.from('drawing_version').select('id, label, issued_at, file_path, file_type, dxf_unit_code, dxf_unit_source').eq('drawing_id', drawingId).order('issued_at', { ascending: false })
    if (error) onError(error.message)
    else setVersions((prev) => ({ ...prev, [drawingId]: data }))
  }
  async function loadSheets(versionId) {
    const { data, error } = await supabase.from('sheet').select('id, sheet_number, identification_status, page_index').eq('drawing_version_id', versionId).order('page_index')
    if (error) onError(error.message)
    else setSheets((prev) => ({ ...prev, [versionId]: data }))
  }

  async function handleAddDrawing(e) {
    e.preventDefault()
    const { error } = await supabase.from('drawing').insert({ tender_id: tenderId, name: newDrawingName })
    if (error) onError(error.message)
    else { onSuccess(`Drawing "${newDrawingName}" created.`); setNewDrawingName(''); await loadDrawings() }
  }
  async function handleAddVersion(drawingId) {
    if (!newVersionLabel) return
    const { error } = await supabase.from('drawing_version').insert({ drawing_id: drawingId, label: newVersionLabel })
    if (error) onError(error.message)
    else { onSuccess(`Version "${newVersionLabel}" issued.`); setNewVersionLabel(''); await loadVersions(drawingId) }
  }
  // Replace File / Create New Revision — reuses the exact same insert
  // shape as handleAddVersion (no in-place file replacement, no
  // Storage upsert/update, no touching the existing version's
  // file_path). The label is auto-generated ("Rev N") since this is a
  // one-click action, not the manual-label form flow.
  async function handleReplaceFile(drawingId) {
    const existing = versions[drawingId] || []
    const nextRevNumber = existing.length + 1
    // User now chooses the new revision's label freely, matching the
    // manual "Add Version" flow's freedom — "Rev N" is only offered as
    // a starting suggestion in the prompt, not silently forced.
    const label = window.prompt('This will create a NEW drawing revision (the existing revision and its measurements remain unchanged). Name for the new revision:', `Rev ${nextRevNumber}`)
    if (!label) return // user cancelled the naming prompt — no version created at all.
    const { data, error } = await supabase.from('drawing_version').insert({ drawing_id: drawingId, label }).select('id').single()
    if (error) { onError(error.message); return }
    onSuccess(`New revision "${label}" created. Existing revisions and their measurements remain unchanged.`)
    await loadVersions(drawingId)
    setExpandedVersion(data.id) // auto-expand the new (empty) version so its PdfUploadInline is immediately visible — same existing mechanism used for any manually-created version, nothing new introduced.
  }
  // Rename Drawing Version — updates ONLY drawing_version.label.
  // Per the confirmed Architecture Check, label is display-only: it is
  // never read by latest_valid_sheet, isOutdated comparisons, sheet/
  // measurement relationships, or any aggregation/BOQ logic — all of
  // those key off id/issued_at/sheet_number, never label. This is
  // therefore a pure display-text update with zero downstream effect
  // on any existing relationship or calculation.
  async function handleRenameVersion(drawingId, versionId, currentLabel) {
    const newLabel = window.prompt('New name for this drawing version:', currentLabel || '')
    if (!newLabel || newLabel === currentLabel) return
    const { error } = await supabase.from('drawing_version').update({ label: newLabel }).eq('id', versionId)
    if (error) { onError(error.message); return }
    onSuccess(`Renamed to "${newLabel}".`)
    await loadVersions(drawingId)
  }
  // Rename Drawing — same safety rationale as Rename Version above:
  // drawing.name is read only for display (Sidebar "Name" row, page
  // header, navigator list) — it is never used in any WHERE/eq()
  // matching, unlike sheet_number. Updating it therefore cannot affect
  // any relationship, aggregation, or "Newer revision available" logic.
  async function handleRenameDrawing(drawingId, currentName) {
    const newName = window.prompt('New name for this drawing:', currentName || '')
    if (!newName || newName === currentName) return
    const { error } = await supabase.from('drawing').update({ name: newName }).eq('id', drawingId)
    if (error) { onError(error.message); return }
    onSuccess(`Renamed to "${newName}".`)
    await loadDrawings()
  }
  // Rename Sheet Number — UNLIKE label/drawing.name, sheet_number is a
  // real logical matching key: latest_valid_sheet and the isOutdated
  // ("Newer revision available") comparison both filter/group by
  // sheet_number across drawing_versions. Renaming it is still SAFE
  // for existing measurements (they key off sheet_id, a stable UUID,
  // never sheet_number), but it deliberately changes which sheets are
  // considered "the same sheet across revisions" going forward — that
  // is the whole point of exposing this control, not a side effect to
  // hide. The confirmation text makes this explicit rather than
  // treating it as a purely cosmetic rename like the two above.
  async function handleRenameSheet(versionId, sheetId, currentNumber) {
    const newNumber = window.prompt('New sheet number:\n\nNote: this affects which sheets are matched as "the same sheet" across drawing revisions (used by the "Newer revision available" indicator). Existing measurements on this sheet are unaffected.', currentNumber || '')
    if (!newNumber || newNumber === currentNumber) return
    const { error } = await supabase.from('sheet').update({ sheet_number: newNumber }).eq('id', sheetId)
    if (error) { onError(error.message); return }
    onSuccess(`Sheet renamed to "${newNumber}".`)
    await loadSheets(versionId)
  }
  async function handleAddSheet(versionId) {
    if (!newSheetNumber) return
    const existing = sheets[versionId] || []
    const { error } = await supabase.from('sheet').insert({ drawing_version_id: versionId, sheet_number: newSheetNumber, page_index: existing.length, identification_status: 'pending' })
    if (error) onError(error.message)
    else { onSuccess(`Sheet "${newSheetNumber}" added.`); setNewSheetNumber(''); await loadSheets(versionId) }
  }
  async function handleIdentifySheet(sheetId, versionId) {
    const { data: authData } = await supabase.auth.getUser()
    const { error } = await supabase.from('sheet').update({ identification_status: 'identified', identified_by: authData.user?.id, identified_at: new Date().toISOString() }).eq('id', sheetId)
    if (error) onError(error.message)
    else { onSuccess('Sheet identified.'); await loadSheets(versionId) }
  }

  async function handleSelectSheet(sheet, version, drawing) {
    let fileUrl = null
    if (version.file_path) {
      const { data: signed, error: signErr } = await supabase.storage.from('drawings').createSignedUrl(version.file_path, 300)
      if (signErr) { onError(signErr.message); return }
      fileUrl = signed.signedUrl
    }
    const { data: latest } = await supabase.from('latest_valid_sheet').select('drawing_version_id').eq('drawing_id', drawing.id).eq('sheet_number', sheet.sheet_number).maybeSingle()

    onSelectSheet({
      id: sheet.id, sheetNumber: sheet.sheet_number, pageIndex: sheet.page_index,
      drawingId: drawing.id, drawingName: drawing.name, versionId: version.id, versionLabel: version.label,
      fileUrl, isOutdated: latest && latest.drawing_version_id !== version.id,
      fileType: version.file_type || 'pdf', // Package 9-10
      dxfUnitCode: version.dxf_unit_code ?? null,
      dxfUnitSource: version.dxf_unit_source ?? null,
    })
  }

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${colors.border}`, fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Drawings
      </div>

      <form onSubmit={handleAddDrawing} style={{ display: 'flex', gap: 6, padding: 10, borderBottom: `1px solid ${colors.border}` }}>
        <Input value={newDrawingName} onChange={(e) => setNewDrawingName(e.target.value)} placeholder="New drawing" required style={{ flex: 1, fontSize: 12 }} />
        <Button type="submit" size="sm">+</Button>
      </form>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        {drawings === null && <div style={{ padding: 14 }}><SkeletonRows rows={2} cols={1} /></div>}
        {drawings?.length === 0 && <div style={{ padding: 16 }}><EmptyState title="No drawings" /></div>}

        {drawings?.map((d) => (
          <div key={d.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                onClick={() => { const opening = expandedDrawing !== d.id; setExpandedDrawing(opening ? d.id : null); if (opening && !versions[d.id]) loadVersions(d.id) }}
                style={{ flex: 1, textAlign: 'left', padding: '8px 12px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <span style={{ fontSize: 12, color: colors.textMuted, marginRight: 5 }}>{expandedDrawing === d.id ? 'v' : '>'}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>{d.name}</span>
              </button>
              <button
                onClick={() => handleRenameDrawing(d.id, d.name)}
                title="Rename this drawing"
                style={{ fontSize: 11, color: colors.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 10px' }}
              >
                ✎
              </button>
            </div>

            {expandedDrawing === d.id && (
              <div style={{ paddingLeft: 16, paddingBottom: 6 }}>
                <div style={{ display: 'flex', gap: 5, padding: '4px 12px 4px 0' }}>
                  <Input value={newVersionLabel} onChange={(e) => setNewVersionLabel(e.target.value)} placeholder="Rev label" style={{ flex: 1, fontSize: 11, padding: '4px 6px' }} />
                  <Button size="sm" variant="secondary" onClick={() => handleAddVersion(d.id)}>+</Button>
                </div>

                {versions[d.id]?.map((v) => (
                  <div key={v.id}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <button
                        onClick={() => { const opening = expandedVersion !== v.id; setExpandedVersion(opening ? v.id : null); if (opening && !sheets[v.id]) loadSheets(v.id) }}
                        style={{ flex: 1, textAlign: 'left', padding: '5px 12px 5px 6px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        <span style={{ fontSize: 11, color: colors.textMuted, marginRight: 5 }}>{expandedVersion === v.id ? 'v' : '>'}</span>
                        <span style={{ fontSize: 12, color: colors.textPrimary }}>{v.label}</span>
                        {!v.file_path && <span style={{ fontSize: 10, color: colors.warningText, marginLeft: 6 }}>no PDF</span>}
                      </button>
                      <button
                        onClick={() => handleRenameVersion(d.id, v.id, v.label)}
                        title="Rename this version"
                        style={{ fontSize: 11, color: colors.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}
                      >
                        ✎
                      </button>
                    </div>

                    {expandedVersion === v.id && (
                      <div style={{ paddingLeft: 18 }}>
                        {!v.file_path && <PdfUploadInline tenderId={tenderId} drawingId={d.id} versionId={v.id} onUploaded={() => loadVersions(d.id)} onError={onError} onSuccess={onSuccess} />}
                        {v.file_path && (
                          <button
                            onClick={() => handleReplaceFile(d.id)}
                            style={{ display: 'block', fontSize: 11, color: colors.textSecondary, background: 'none', border: 'none', cursor: 'pointer', padding: '3px 12px 3px 6px', textDecoration: 'underline' }}
                          >
                            Replace File / Create New Revision
                          </button>
                        )}
                        <div style={{ display: 'flex', gap: 5, padding: '3px 12px 5px 0' }}>
                          <Input value={newSheetNumber} onChange={(e) => setNewSheetNumber(e.target.value)} placeholder="Sheet no." style={{ flex: 1, fontSize: 11, padding: '4px 6px' }} />
                          <Button size="sm" variant="secondary" onClick={() => handleAddSheet(v.id)}>+</Button>
                        </div>
                        {sheets[v.id]?.map((s) => (
                          <div
                            key={s.id}
                            onClick={() => s.identification_status === 'identified' && handleSelectSheet(s, v, d)}
                            style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 12px 5px 6px', borderRadius: 4,
                              background: selectedSheetId === s.id ? '#EFF4FC' : 'transparent',
                              cursor: s.identification_status === 'identified' ? 'pointer' : 'default',
                            }}
                          >
                            <span style={{ fontSize: 12, color: colors.textPrimary }}>{s.sheet_number}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {s.identification_status === 'identified'
                                ? <span style={{ fontSize: 10, fontWeight: 600, color: colors.accentGreen }}>OK</span>
                                : <button onClick={(e) => { e.stopPropagation(); handleIdentifySheet(s.id, v.id) }} style={{ fontSize: 10, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Identify</button>}
                              <button onClick={(e) => { e.stopPropagation(); handleRenameSheet(v.id, s.id, s.sheet_number) }} title="Rename sheet number" style={{ fontSize: 10, color: colors.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}>✎</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function PdfUploadInline({ tenderId, drawingId, versionId, onUploaded, onError, onSuccess }) {
  const [uploading, setUploading] = useState(false)
  async function handleFileChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    // DXF support (Package 9-10): accept .dxf alongside .pdf. file_type
    // is derived from the actual filename extension, not assumed.
    const isDxf = file.name.toLowerCase().endsWith('.dxf')
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    if (!isDxf && !isPdf) { onError('Only PDF or DXF files are supported.'); return }
    setUploading(true)
    const ext = isDxf ? 'dxf' : 'pdf'
    const path = `${tenderId}/${drawingId}/${versionId}.${ext}`
    const { error: uploadError } = await supabase.storage.from('drawings').upload(path, file, { contentType: isDxf ? 'application/dxf' : 'application/pdf', upsert: false })
    if (uploadError) { onError(uploadError.message); setUploading(false); return }
    const { error: updateError } = await supabase.from('drawing_version').update({ file_path: path, file_type: ext }).eq('id', versionId)
    if (updateError) onError(updateError.message)
    else { onSuccess(`${isDxf ? 'DXF' : 'PDF'} uploaded.`); onUploaded() }
    setUploading(false)
  }
  return (
    <label style={{ display: 'block', fontSize: 11, color: colors.primary, cursor: 'pointer', fontWeight: 600, padding: '3px 12px 3px 6px' }}>
      {uploading ? 'Uploading...' : 'Upload PDF'}
      <input type="file" accept="application/pdf,.dxf" onChange={handleFileChange} disabled={uploading} style={{ display: 'none' }} />
    </label>
  )
}

// =======================================================================
// CENTER + RIGHT: The single canvas workspace + contextual sidebar.
// =======================================================================
function CanvasWorkspace({ tenderId, revisionId, selectedSheet, selectedTakeoffItemId, setSelectedTakeoffItemId, selectedMeasurementId, selectedMeasurementIds, setSelectedMeasurementId, setSelectedMeasurementIds, onError, onSuccess, rightCollapsed, rightSidebarCollapsed, setRightSidebarCollapsed, fullCanvasMode, setFullCanvasMode, isolateActive, setIsolateActive, lockedIds, setLockedIds, opacityMap, setOpacityMap }) {
  // PHASE 2 — the ONE place that mutates the selection, guaranteeing the
  // Invariant (Primary === null iff set is empty; Primary always a
  // member) can never be violated by a scattered update elsewhere.
  // `opts` stays backward-compatible: existing callers that pass only
  // (id) or (id, {requestDelete}) are unaffected — Ctrl/Shift behavior
  // only activates when explicitly reported by the interaction surface.
  function applySelectionEvent(clickedId, opts) {
    if (opts?.requestDelete) {
      // Existing delete-request path — untouched, still Primary-based.
      return
    }
    if (clickedId === null) {
      // Empty click / Escape: clear everything.
      setSelectedMeasurementIds([])
      setSelectedMeasurementId(null)
      return
    }
    if (opts?.ctrlOrCmd) {
      setSelectedMeasurementIds((prev) => {
        const isAlreadySelected = prev.includes(clickedId)
        if (isAlreadySelected) {
          const next = prev.filter((id) => id !== clickedId)
          // Removing a non-Primary leaves Primary unchanged; removing
          // the Primary promotes the most recently added remaining
          // item (the new array's last element); empty -> null.
          setSelectedMeasurementId((currentPrimary) =>
            currentPrimary === clickedId ? (next.length > 0 ? next[next.length - 1] : null) : currentPrimary
          )
          return next
        }
        const next = [...prev, clickedId]
        setSelectedMeasurementId(clickedId) // newly added becomes Primary.
        return next
      })
      return
    }
    if (opts?.shift) {
      setSelectedMeasurementIds((prev) => {
        if (prev.includes(clickedId)) return prev // Shift never removes, never changes Primary here.
        setSelectedMeasurementId(clickedId) // newly added by shift becomes Primary.
        return [...prev, clickedId]
      })
      return
    }
    // Plain click replaces the entire selection.
    setSelectedMeasurementIds([clickedId])
    setSelectedMeasurementId(clickedId)
  }

  // PHASE 3 — Marquee (Window/Crossing Select) selection result. Applies
  // the SAME Invariant as applySelectionEvent (Primary always the last
  // element of the ordered array, always a member, empty -> null), but
  // for a batch of ids at once rather than a single click. Non-additive
  // (normal drag): replaces the whole selection with matchedIds, in the
  // order TakeoffCanvas discovered them (stable — filtered from the
  // already-deterministically-ordered existingMeasurements array), so
  // the last discovered match becomes Primary. Additive (Ctrl/Cmd drag):
  // appends only the NEW ids (not already selected) to the existing
  // ordered array, preserving the current Primary unless new ids were
  // actually added, in which case the last newly-added id becomes
  // Primary — consistent with how Ctrl+Click already behaves for a
  // single id in applySelectionEvent.
  function applyMarqueeSelection(matchedIds, opts) {
    if (!opts?.additive) {
      setSelectedMeasurementIds(matchedIds)
      setSelectedMeasurementId(matchedIds.length > 0 ? matchedIds[matchedIds.length - 1] : null)
      return
    }
    setSelectedMeasurementIds((prev) => {
      const newOnes = matchedIds.filter((id) => !prev.includes(id))
      if (newOnes.length === 0) return prev
      const next = [...prev, ...newOnes]
      setSelectedMeasurementId(newOnes[newOnes.length - 1])
      return next
    })
  }

  const [takeoffItems, setTakeoffItems] = useState([])
  const [boqItems, setBoqItems] = useState([])
  const [calibrations, setCalibrations] = useState([])
  const [measurements, setMeasurements] = useState([])
  const [aggregatedQuantity, setAggregatedQuantity] = useState(undefined)
  const [testExpectedInput, setTestExpectedInput] = useState('')
  const [calibrationTest, setCalibrationTest] = useState(null)
  const [calibratePrompt, setCalibratePrompt] = useState(null) // { pdfDist, realDistance, unit, error } | null
  const [hiddenIds, setHiddenIds] = useState(new Set())
  const [hiddenLayers, setHiddenLayers] = useState(new Set())
  const [historyOpen, setHistoryOpen] = useState(false)

  const selectedItem = takeoffItems.find((i) => i.id === selectedTakeoffItemId)

  const loadTakeoffItems = useCallback(async () => {
    const { data, error } = await supabase.from('takeoff_item').select('id, description, unit, measurement_type').eq('tender_id', tenderId).order('created_at')
    if (error) return onError(error.message)
    setTakeoffItems(data)
    if (!selectedTakeoffItemId && data.length > 0) setSelectedTakeoffItemId(data[0].id)
  }, [tenderId, onError, selectedTakeoffItemId, setSelectedTakeoffItemId])

  useEffect(() => { loadTakeoffItems() }, [loadTakeoffItems])

  useEffect(() => {
    supabase.from('boq_item').select('id, item_no, description, resolved_quantity').eq('estimate_revision_id', revisionId).order('item_no')
      .then(({ data, error }) => { if (error) onError(error.message); else setBoqItems(data) })
  }, [revisionId, onError])

  const loadCalibrations = useCallback(async () => {
    if (!selectedSheet) return setCalibrations([])
    // Load the FULL history (active + inactive) so the sidebar can show
    // Calibration History & Cross-Validation. The active calibration
    // (is_active = true) remains the only one ever used by the Takeoff
    // engine — enforced both here and by the DB's partial unique index.
    const { data, error } = await supabase
      .from('sheet_calibration')
      .select('id, pixel_distance, real_distance, unit, is_active, created_at')
      .eq('sheet_id', selectedSheet.id)
      .order('created_at', { ascending: false })
    if (error) onError(error.message)
    else setCalibrations(data || [])
  }, [selectedSheet, onError])

  useEffect(() => { loadCalibrations() }, [loadCalibrations])

  const loadMeasurements = useCallback(async () => {
    if (!selectedTakeoffItemId) { setMeasurements([]); return }
    const { data, error } = await supabase
      .from('measurement')
      .select('id, calculated_value, is_active, sheet_id, geometry_data, measurement_type, created_at, created_by, superseded_by, superseded_reason, calibration_id, source_type, takeoff_item_id, polarity, review_status, rejection_reason')
      .eq('takeoff_item_id', selectedTakeoffItemId)
      .order('created_at')
    if (error) return onError(error.message)
    setMeasurements(data)

    const { data: agg } = await supabase.from('takeoff_item_aggregated_quantity').select('aggregated_quantity').eq('takeoff_item_id', selectedTakeoffItemId).maybeSingle()
    setAggregatedQuantity(agg?.aggregated_quantity)
  }, [selectedTakeoffItemId, onError])

  useEffect(() => { loadMeasurements(); setSelectedMeasurementId(null) }, [loadMeasurements, setSelectedMeasurementId])

  const activeMeasurements = measurements.filter((m) => m.is_active)
  const historyMeasurements = measurements.filter((m) => !m.is_active)
  const measurementsOnThisSheet = activeMeasurements.filter((m) => m.sheet_id === selectedSheet?.id)
  const activeCalibration = calibrations.find((c) => c.is_active)
  const calibrationHistory = calibrations.filter((c) => !c.is_active)

  function handleTestMeasurement(measuredValue) {
    const expected = Number(testExpectedInput)
    if (!expected) { alert('Enter an expected value first.'); return }
    const diff = measuredValue - expected
    const accuracy = 100 - (Math.abs(diff) / expected) * 100
    setCalibrationTest({ expected, measured: measuredValue, diff, accuracy })
  }

  function handleCalibrate(pdfDist) {
    if (pdfDist < 1) { onError('Invalid calibration - points too close together.'); return }
    setCalibratePrompt({ pdfDist, realDistance: '', unit: 'm', error: '' })
  }

  async function confirmCalibrate() {
    const { pdfDist, realDistance: realDistanceStr, unit: unitRaw } = calibratePrompt
    const realDistance = Number(realDistanceStr)
    if (!realDistanceStr || isNaN(realDistance) || realDistance <= 0) {
      setCalibratePrompt((prev) => ({ ...prev, error: 'Enter a positive number for the real-world distance.' }))
      return
    }
    const unit = (unitRaw || 'm').trim() || 'm'
    setCalibratePrompt(null)

    // Never trust local React state for the DECISION of whether a previous
    // active calibration exists — that check still queries the DB directly
    // so the deactivate-then-insert sequence is correct regardless of
    // local state timing. But once both writes succeed, we update the
    // `calibrations` state directly from the rows Postgres just returned
    // (via .select() on each write) instead of re-querying afterwards —
    // this is what actually fixes the "needs F5" symptom: a fresh SELECT
    // right after a write is not guaranteed to reflect it in the local
    // React state update cycle as reliably as using the write's own
    // returned row.
    const { data: currentActiveRows, error: fetchActiveErr } = await supabase
      .from('sheet_calibration')
      .select('id')
      .eq('sheet_id', selectedSheet.id)
      .eq('is_active', true)

    if (fetchActiveErr) {
      onError(`Could not verify current calibration state: ${fetchActiveErr.message}`)
      return
    }

    let deactivatedRows = []
    if (currentActiveRows && currentActiveRows.length > 0) {
      const { data: updatedRows, error: deactivateErr } = await supabase
        .from('sheet_calibration')
        .update({ is_active: false })
        .in('id', currentActiveRows.map((r) => r.id))
        .select('id, pixel_distance, real_distance, unit, is_active, created_at')
      if (deactivateErr) {
        onError(`Recalibration failed — could not deactivate the previous calibration: ${deactivateErr.message}`)
        return
      }
      deactivatedRows = updatedRows || []
    }

    const { data: newRow, error: insertErr } = await supabase
      .from('sheet_calibration')
      .insert({ sheet_id: selectedSheet.id, pixel_distance: pdfDist, real_distance: realDistance, unit })
      .select('id, pixel_distance, real_distance, unit, is_active, created_at')
      .single()

    if (insertErr) {
      if (currentActiveRows && currentActiveRows.length > 0) {
        onError(
          `Recalibration failed after deactivating the previous calibration — this sheet now has NO active calibration. ` +
          `Please recalibrate immediately. Underlying error: ${insertErr.message}`
        )
      } else {
        onError(`Calibration save failed: ${insertErr.message}`)
      }
      // Here a re-fetch IS appropriate: we're in an error/inconsistent
      // path and need the true DB state, not an optimistic one.
      await loadCalibrations()
      return
    }

    // Update local state immediately and directly from the rows the
    // database just returned — no re-fetch, no delay, no F5 needed.
    setCalibrations((prev) => {
      const deactivatedIds = new Set(deactivatedRows.map((r) => r.id))
      const withoutDeactivated = prev.filter((c) => !deactivatedIds.has(c.id))
      const merged = [
        newRow,
        ...deactivatedRows,
        ...withoutDeactivated.filter((c) => c.id !== newRow.id),
      ]
      return merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    })

    onSuccess(deactivatedRows.length > 0 ? 'Sheet recalibrated — previous calibration moved to history.' : 'Sheet calibrated.')
  }

  const [pendingVolumeArea, setPendingVolumeArea] = useState(null) // { calculatedValue (area m²), geometryData } awaiting explicit depth

  // Package 9-10 — DXF unit handling. dxfUnitPromptOpen surfaces the
  // "confirm unit" prompt when TakeoffCanvas reports the unit could
  // not be auto-detected (unknown/Unitless/missing HEADER).
  const [dxfUnitPromptOpen, setDxfUnitPromptOpen] = useState(false)
  async function handleDxfUnitDetected(unitCode) {
    // Auto-detected — persist immediately, no user interaction needed.
    const { error } = await supabase.from('drawing_version').update({ dxf_unit_code: unitCode, dxf_unit_source: 'detected' }).eq('id', selectedSheet.versionId)
    if (error) onError(error.message)
  }
  async function handleDxfUnitConfirm(unitCode) {
    const { error } = await supabase.from('drawing_version').update({ dxf_unit_code: unitCode, dxf_unit_source: 'user_confirmed' }).eq('id', selectedSheet.versionId)
    if (error) { onError(error.message); return }
    setDxfUnitPromptOpen(false)
    onSuccess('DXF unit confirmed.')
  }
  const [depthInput, setDepthInput] = useState('')

  async function handleMeasurementComplete({ measurementType, calculatedValue, geometryData }, opts) {
    if (opts?.testMode) { handleTestMeasurement(calculatedValue); return }
    if (!selectedItem) { onError('Select a Takeoff Item first.'); return }

    // Volume items are drawn with the existing Area/Rectangle tool (no new
    // drawing tool). The tool always reports measurementType='area' for
    // the drawn shape — when the target takeoff item is 'volume', that
    // area becomes an input awaiting an explicit depth, rather than being
    // saved directly. Nothing is persisted until depth is provided.
    if (selectedItem.measurement_type === 'volume') {
      if (measurementType !== 'area') {
        onError(`This takeoff item expects an Area/Rectangle shape to derive Volume from — you drew a "${measurementType}".`)
        return
      }
      setPendingVolumeArea({ area: calculatedValue, geometryData })
      setDepthInput('')
      return
    }

    if (measurementType !== selectedItem.measurement_type) {
      onError(`This takeoff item expects "${selectedItem.measurement_type}" - you drew a "${measurementType}".`)
      return
    }
    const { data: authData } = await supabase.auth.getUser()
    const activeCal = calibrations.find((c) => c.is_active)
    const { error } = await supabase.from('measurement').insert({
      sheet_id: selectedSheet.id, takeoff_item_id: selectedTakeoffItemId, calibration_id: activeCal?.id,
      measurement_type: measurementType, geometry_data: geometryData, calculated_value: calculatedValue,
      source_type: 'manual', created_by: authData.user?.id,
    })
    if (error) onError(`Measurement save failed: ${error.message}`)
    else { onSuccess(`Measurement of ${calculatedValue.toFixed(2)} ${selectedItem.unit} added.`); await loadMeasurements() }
  }

  // Package 1c — Duplicate. Mirrors handleMeasurementComplete's insert
  // shape exactly (same table, same required fields, same
  // source_type='manual' since the CHECK constraint on this column
  // only allows 'manual'/'ai_detected' and this is a deliberate manual
  // action) — no parallel creation path. Same sheet_id/calibration_id
  // as the original by construction (TakeoffCanvas passes them
  // through unchanged), so no re-calibration question arises.
  async function handleDuplicateMeasurement(sheetId, takeoffItemId, calibrationId, measurementType, geometryData, calculatedValue) {
    const { data: authData } = await supabase.auth.getUser()
    const { error } = await supabase.from('measurement').insert({
      sheet_id: sheetId, takeoff_item_id: takeoffItemId, calibration_id: calibrationId,
      measurement_type: measurementType, geometry_data: geometryData, calculated_value: calculatedValue,
      source_type: 'manual', created_by: authData.user?.id,
    })
    if (error) { onError(`Duplicate failed: ${error.message}`); return }
    onSuccess(`Duplicated: ${calculatedValue.toFixed(2)}`)
    await loadMeasurements()
  }

  // Package 2 — Toggle Polarity. Direct UPDATE on the existing active
  // record, NOT a Supersede: geometry_data and calculated_value are
  // untouched, so this is a pure classification-field write, not a
  // geometry version transition. No history/Undo/Redo entry.
  async function handleTogglePolarity(measurementId, newPolarity) {
    const { error } = await supabase.from('measurement').update({ polarity: newPolarity }).eq('id', measurementId)
    if (error) { onError(`Polarity update failed: ${error.message}`); return }
    await loadMeasurements()
  }

  // Confirms a pending Volume measurement: computes the authoritative
  // value via the Quantity Engine (never inline arithmetic here) and
  // persists it exactly like any other measurement.
  async function handleConfirmVolume() {
    const depth = Number(depthInput)
    if (depthInput.trim() === '' || Number.isNaN(depth) || depth <= 0) {
      onError('Depth/Height is required and must be greater than 0.')
      return
    }
    let volume
    try {
      volume = calculateVolume(pendingVolumeArea.area, depth)
    } catch (e) {
      onError(e.message)
      return
    }
    const { data: authData } = await supabase.auth.getUser()
    const activeCal = calibrations.find((c) => c.is_active)
    const { error } = await supabase.from('measurement').insert({
      sheet_id: selectedSheet.id, takeoff_item_id: selectedTakeoffItemId, calibration_id: activeCal?.id,
      measurement_type: 'volume',
      geometry_data: { ...pendingVolumeArea.geometryData, depth, depthUnit: 'm' },
      calculated_value: volume,
      source_type: 'manual', created_by: authData.user?.id,
    })
    if (error) {
      onError(`Measurement save failed: ${error.message}`)
    } else {
      onSuccess(`Volume of ${volume.toFixed(2)} ${selectedItem.unit} added.`)
      setPendingVolumeArea(null)
      setDepthInput('')
      await loadMeasurements()
    }
  }

  // BUG FIX (confirmed root cause): this function previously always
  // persisted `old.geometry_data` regardless of whether the caller had
  // edited the geometry — so a vertex drag correctly updated
  // calculated_value but silently discarded the new shape, leaving the
  // visual geometry inconsistent with the saved quantity after reload.
  // `newGeometryData` is now an OPTIONAL third parameter: when a caller
  // (geometry edit) supplies it, that is what gets persisted; when a
  // caller (manual numeric correction, e.g. the direct-value input)
  // omits it, the existing geometry is preserved exactly as before —
  // no behavior change for that path.
  async function handleSupersede(measurementId, newValue, newGeometryData) {
    const old = measurements.find((m) => m.id === measurementId)
    if (!old) return null // caller (Undo/Redo/Vertex edit) treats a null return as failure — never treats a missing local record as success.
    const { data: authData } = await supabase.auth.getUser()
    // Atomic Supersede via RPC (10). Requirement (1): the successor id
    // is the function's return value — setSelectedMeasurementId is a
    // UI side-effect only, never a substitute for the return value the
    // caller needs to keep its own local history references valid.
    const { data: newRow, error: rpcErr } = await supabase.rpc('supersede_measurement', {
      p_measurement_id: measurementId,
      p_new_value: newValue,
      p_new_geometry_data: newGeometryData ?? null,
      p_superseded_reason: 'correction',
      p_created_by: authData.user?.id,
    }).single()
    if (rpcErr) { onError(`Supersede failed: ${rpcErr.message}`); return null } // (I) failed Supersede -> null, caller must not touch its history on this.
    onSuccess(`Superseded: ${old.calculated_value.toFixed(2)} -> ${newValue.toFixed(2)} ${selectedItem?.unit || ''}`)
    setSelectedMeasurementId(newRow.id)
    await loadMeasurements()
    return newRow.id
  }

  async function handleGeometryEdited(measurementId, newGeometryData, newCalculatedValue) {
    return await handleSupersede(measurementId, newCalculatedValue, newGeometryData) // (2) propagates the successor id (or null on failure) to the Canvas caller.
  }

  // Package 4 UI — Batch Submit for Review. Calls the approved
  // submit_for_review(uuid[]) RPC directly (auth.uid() resolved
  // server-side, exactly as designed) — no client-side review state,
  // no separate calculation. Reloads via the SAME loadMeasurements()
  // used everywhere else so the UI is always a mirror of the database.
  async function handleSubmitForReview(measurementIds) {
    if (!measurementIds || measurementIds.length === 0) return
    const { data: submittedCount, error } = await supabase.rpc('submit_for_review', { p_measurement_ids: measurementIds })
    if (error) { onError(`Submit for review failed: ${error.message}`); return }
    onSuccess(`${submittedCount} measurement${submittedCount !== 1 ? 's' : ''} submitted for review.`)
    await loadMeasurements()
  }

  async function handleCapture(boqItemId) {
    if (aggregatedQuantity === undefined || !boqItemId) return
    // Review check: the quantity counts every active measurement except
    // Rejected ones, so Unreviewed/Pending measurements are inside it. Tell
    // the user how many, and capture only if they explicitly continue. The
    // quantity itself is never changed here -- it comes from the database
    // view. If the check cannot be made, capture is cancelled rather than
    // silently skipping the warning.
    const { data: openRows, error: openErr } = await supabase
      .from('measurement')
      .select('review_status')
      .eq('takeoff_item_id', selectedTakeoffItemId)
      .eq('is_active', true)
      .in('review_status', OPEN_REVIEW_STATUSES)
    if (openErr) { onError(`Could not check measurement review status, capture cancelled: ${openErr.message}`); return }
    const reviewWarning = buildCaptureReviewWarning(countOpenReviews(openRows))
    if (reviewWarning && !window.confirm(reviewWarning)) return
    const { data: authData } = await supabase.auth.getUser()
    const { error } = await supabase.rpc('recapture_quantity_source', {
      p_boq_item_id: boqItemId, p_source_type: 'takeoff_derived', p_captured_value: aggregatedQuantity,
      p_takeoff_item_id: selectedTakeoffItemId, p_captured_by: authData.user?.id,
    })
    if (error) onError(error.message)
    else {
      onSuccess(`Captured ${formatNumber(aggregatedQuantity)} into BOQ item.`)
      const { data } = await supabase.from('boq_item').select('id, item_no, description, resolved_quantity').eq('estimate_revision_id', revisionId).order('item_no')
      setBoqItems(data)
    }
  }

  async function handleNewTakeoffItem(description, unit, measurementType) {
    const { error } = await supabase.from('takeoff_item').insert({ tender_id: tenderId, description, unit, measurement_type: measurementType })
    if (error) onError(error.message)
    else { onSuccess(`Takeoff item "${description}" created.`); await loadTakeoffItems() }
  }

  if (!selectedSheet) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <EmptyState title="Select a sheet" description="Choose an identified sheet from the navigator on the left to begin takeoff." />
      </div>
    )
  }

  return (
    <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {pendingVolumeArea && (
          <VolumeDepthPrompt
            area={pendingVolumeArea.area}
            unit={selectedItem?.unit}
            depthInput={depthInput}
            onDepthChange={setDepthInput}
            onConfirm={handleConfirmVolume}
            onCancel={() => { setPendingVolumeArea(null); setDepthInput('') }}
          />
        )}
        {dxfUnitPromptOpen && (
          <DxfUnitPrompt onConfirm={handleDxfUnitConfirm} onCancel={() => setDxfUnitPromptOpen(false)} />
        )}
        {calibratePrompt && (
          <CalibratePrompt
            realDistance={calibratePrompt.realDistance}
            unit={calibratePrompt.unit}
            error={calibratePrompt.error}
            onDistanceChange={(v) => setCalibratePrompt((prev) => ({ ...prev, realDistance: v, error: '' }))}
            onUnitChange={(v) => setCalibratePrompt((prev) => ({ ...prev, unit: v }))}
            onConfirm={confirmCalibrate}
            onCancel={() => setCalibratePrompt(null)}
          />
        )}
        <TakeoffCanvas
          fileUrl={selectedSheet.fileUrl}
          pageIndex={selectedSheet.pageIndex}
          fileType={selectedSheet.fileType}
          dxfUnitCode={selectedSheet.dxfUnitCode}
          dxfUnitSource={selectedSheet.dxfUnitSource}
          onDxfUnitDetected={handleDxfUnitDetected}
          onDxfUnitRequired={() => setDxfUnitPromptOpen(true)}
          calibration={activeCalibration ? { pdf_distance: activeCalibration.pixel_distance, real_distance: activeCalibration.real_distance, unit: activeCalibration.unit } : null}
          onCalibrate={handleCalibrate}
          onMeasurementComplete={handleMeasurementComplete}
          expectedMeasurementType={selectedItem?.measurement_type}
          existingMeasurements={measurementsOnThisSheet}
          onSelectMeasurement={(id, opts) => {
            if (opts?.requestDelete && selectedMeasurementId) {
              if (lockedIds.has(selectedMeasurementId)) { onError('This measurement is locked and cannot be modified.'); return } // Package 3
              const v = window.prompt('Corrected value (supersedes this measurement, not delete):')
              if (v && !isNaN(Number(v))) handleSupersede(selectedMeasurementId, Number(v))
              return
            }
            applySelectionEvent(id, opts)
          }}
          onMarqueeSelect={applyMarqueeSelection}
          onDuplicateMeasurement={handleDuplicateMeasurement}
          onTogglePolarity={handleTogglePolarity}
          onSubmitForReview={handleSubmitForReview}
          lockedIds={lockedIds}
          isolateActive={isolateActive}
          selectedMeasurementId={selectedMeasurementId}
          selectedMeasurementIds={selectedMeasurementIds}
          onGeometryEdited={handleGeometryEdited}
          hiddenIds={hiddenIds}
          hiddenLayers={hiddenLayers}
          setHiddenLayers={setHiddenLayers}
          setIsolateActive={setIsolateActive}
          setLockedIds={setLockedIds}
          opacityMap={opacityMap}
          setOpacityMap={setOpacityMap}
          rightCollapsed={rightCollapsed}
          fullCanvasMode={fullCanvasMode}
          setFullCanvasMode={setFullCanvasMode}
          testMode={!!calibrationTest || testExpectedInput !== ''}
        />
      </div>

      <div style={{ flexShrink: 0, position: 'relative', width: rightCollapsed ? 36 : 300, minHeight: 0, transition: 'width 0.15s ease' }}>
        <button
          onClick={() => setRightSidebarCollapsed((c) => !c)}
          disabled={fullCanvasMode}
          title={rightCollapsed ? 'Expand properties panel' : 'Collapse properties panel'}
          style={{ position: 'absolute', top: 6, left: rightCollapsed ? 4 : 6, zIndex: 2, width: 24, height: 24, borderRadius: 6, border: `1px solid ${colors.border}`, background: '#fff', cursor: fullCanvasMode ? 'not-allowed' : 'pointer', fontSize: 12, opacity: fullCanvasMode ? 0.4 : 1 }}
        >
          {rightCollapsed ? '«' : '»'}
        </button>
        {!rightCollapsed && (
          <TakeoffSidebar
            selectedSheet={selectedSheet}
            calibration={activeCalibration}
            calibrationHistory={calibrationHistory}
            takeoffItems={takeoffItems}
            selectedTakeoffItemId={selectedTakeoffItemId}
            onSelectTakeoffItem={setSelectedTakeoffItemId}
            onNewTakeoffItem={handleNewTakeoffItem}
            selectedItem={selectedItem}
            aggregatedQuantity={aggregatedQuantity}
            boqItems={boqItems}
            onCapture={handleCapture}
            activeMeasurements={activeMeasurements}
            historyMeasurements={historyMeasurements}
            allMeasurements={measurements}
            historyOpen={historyOpen}
            setHistoryOpen={setHistoryOpen}
            selectedMeasurementId={selectedMeasurementId}
            onSelectMeasurement={setSelectedMeasurementId}
            hiddenIds={hiddenIds}
            onToggleHidden={(id) => setHiddenIds((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })}
            hiddenLayers={hiddenLayers}
            onToggleLayer={(layer) => setHiddenLayers((prev) => { const next = new Set(prev); next.has(layer) ? next.delete(layer) : next.add(layer); return next })}
            testExpectedInput={testExpectedInput}
            setTestExpectedInput={setTestExpectedInput}
            calibrationTest={calibrationTest}
            onClearTest={() => { setCalibrationTest(null); setTestExpectedInput('') }}
            onSupersede={handleSupersede}
            lockedIds={lockedIds}
            opacityMap={opacityMap}
            setOpacityMap={setOpacityMap}
            setLockedIds={setLockedIds}
          />
        )}
      </div>
    </div>
  )
}

// =======================================================================
// RIGHT SIDEBAR - contextual: Drawing / Calibration / Active Takeoff Item
// (with a selector) / Measurements (Active list + collapsible History).
// =======================================================================
// =======================================================================
// VolumeDepthPrompt — shown after the user finishes drawing an Area/
// Rectangle shape on a 'volume' takeoff item. It NEVER computes the
// volume itself; the live preview below calls the same calculateVolume()
// function from the Quantity Engine that the actual save path uses, so
// there is exactly one source of truth for the formula, not two.
// =======================================================================
// Package 9-10 — shown when TakeoffCanvas reports the DXF's unit
// could not be auto-detected (Unitless, missing HEADER, or an
// unsupported/unrecognized $INSUNITS code). The user MUST pick a
// unit before measurements from this drawing are dimensionally
// reliable — never silently assumed.
const DXF_UNIT_OPTIONS = [
  { code: 1, label: 'Inches' }, { code: 2, label: 'Feet' }, { code: 4, label: 'Millimeters' },
  { code: 5, label: 'Centimeters' }, { code: 6, label: 'Meters' }, { code: 10, label: 'Yards' },
]
function DxfUnitPrompt({ onConfirm, onCancel }) {
  const [selected, setSelected] = useState(6)
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: 340 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Confirm DXF Drawing Unit</div>
        <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 12 }}>
          This DXF file does not declare a reliable unit. Select the correct drawing unit before measurements can be trusted.
        </p>
        <Select value={selected} onChange={(e) => setSelected(Number(e.target.value))} style={{ width: '100%', marginBottom: 14 }}>
          {DXF_UNIT_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
        </Select>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onConfirm(selected)}>Confirm</Button>
        </div>
      </div>
    </div>
  )
}

function VolumeDepthPrompt({ area, unit, depthInput, onDepthChange, onConfirm, onCancel }) {
  const depth = Number(depthInput)
  const depthValid = depthInput.trim() !== '' && !Number.isNaN(depth) && depth > 0
  let previewVolume = null
  if (depthValid) {
    try { previewVolume = calculateVolume(area, depth) } catch { previewVolume = null }
  }

  return (
    <div style={{
      position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 50,
      background: '#fff', border: `1.5px solid ${colors.primary}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
      width: 340, padding: 16,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 10 }}>Volume — Enter Depth</div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: `1px dashed ${colors.border}` }}>
        <span style={{ color: colors.textMuted }}>Area (Canvas)</span>
        <strong style={{ color: colors.textPrimary }}>{formatNumber(area)} m²</strong>
      </div>

      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginTop: 12, marginBottom: 5 }}>
        Depth / Height (m) — required
      </label>
      <Input
        type="number" step="any" autoFocus value={depthInput} onChange={(e) => onDepthChange(e.target.value)}
        placeholder="e.g. 0.30" style={{ width: '100%', fontSize: 14 }}
      />

      {depthInput.trim() !== '' && !depthValid && (
        <div style={{ fontSize: 11, color: colors.danger, marginTop: 4 }}>Depth must be a positive number.</div>
      )}

      {previewVolume != null && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: colors.textMuted }}>{formatNumber(area)} m² × {formatNumber(depth)} m =</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: colors.primary }}>{formatNumber(previewVolume)} m³</div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={onConfirm} disabled={!depthValid}>Save Measurement</Button>
      </div>
    </div>
  )
}

function CalibratePrompt({ realDistance, unit, error, onDistanceChange, onUnitChange, onConfirm, onCancel }) {
  const dist = Number(realDistance)
  const distValid = realDistance.trim() !== '' && !Number.isNaN(dist) && dist > 0

  return (
    <div style={{
      position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 50,
      background: '#fff', border: `1.5px solid ${colors.primary}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
      width: 340, padding: 16,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 10 }}>Calibrate Sheet</div>

      <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>
        Enter the real-world distance the line you just drew represents.
      </div>

      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>
        Real-world distance — required
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          type="number" step="any" autoFocus value={realDistance}
          onChange={(e) => onDistanceChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && distValid) onConfirm() }}
          placeholder="e.g. 10" style={{ flex: 1, fontSize: 14 }}
        />
        <Input
          type="text" value={unit} onChange={(e) => onUnitChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && distValid) onConfirm() }}
          placeholder="m" style={{ width: 64, fontSize: 14 }}
        />
      </div>

      {error && (
        <div style={{ fontSize: 11, color: colors.danger, marginTop: 6 }}>{error}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={onConfirm} disabled={!distValid}>Set Calibration</Button>
      </div>
    </div>
  )
}

function TakeoffSidebar({
  selectedSheet, calibration, calibrationHistory, takeoffItems, selectedTakeoffItemId, onSelectTakeoffItem, onNewTakeoffItem,
  selectedItem, aggregatedQuantity, boqItems, onCapture,
  activeMeasurements, historyMeasurements, allMeasurements, historyOpen, setHistoryOpen,
  selectedMeasurementId, onSelectMeasurement, hiddenIds, onToggleHidden, hiddenLayers, onToggleLayer,
  testExpectedInput, setTestExpectedInput, calibrationTest, onClearTest, onSupersede,
  lockedIds = new Set(), setLockedIds, opacityMap = new Map(), setOpacityMap,
}) {
  const [newItemOpen, setNewItemOpen] = useState(false)
  const [newDesc, setNewDesc] = useState('')
  const [newUnit, setNewUnit] = useState('')
  const [newType, setNewType] = useState('area')
  const [captureBoqId, setCaptureBoqId] = useState('')
  const [measurementSearchQuery, setMeasurementSearchQuery] = useState('')

  const scaleFactor = calibration ? calibration.real_distance / calibration.pixel_distance : null

  const typeCounters = {}
  const displayIds = {}
  const typePrefix = { area: 'AREA', length: 'LEN', count: 'COUNT' }
  activeMeasurements.forEach((m) => {
    typeCounters[m.measurement_type] = (typeCounters[m.measurement_type] || 0) + 1
    displayIds[m.id] = `${typePrefix[m.measurement_type] || 'MEAS'}-${String(typeCounters[m.measurement_type]).padStart(3, '0')}`
  })

  return (
    <div style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12, height: '100%', overflowY: 'auto' }}>
      <SidebarSection title="Drawing">
        <SidebarRow label="Name" value={selectedSheet.drawingName || '-'} />
        <SidebarRow label="Revision" value={selectedSheet.versionLabel || '-'} />
        <SidebarRow label="Sheet" value={selectedSheet.sheetNumber || '-'} />
      </SidebarSection>

      <SidebarSection title="Calibration">
        {calibration ? (
          <>
            <SidebarRow label="Status" value="Calibrated" valueColor={colors.accentGreen} />
            <SidebarRow label="Scale" value={`1 unit = ${scaleFactor.toFixed(4)} ${calibration.unit}`} />
            <SidebarRow label="Reference" value={`${formatNumber(calibration.real_distance)} ${calibration.unit}`} />
            <SidebarRow label="PDF Distance" value={`${formatNumber(calibration.pixel_distance)} units`} />

            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${colors.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 6 }}>Test Measurement</div>
              <Input type="number" step="any" value={testExpectedInput} onChange={(e) => setTestExpectedInput(e.target.value)} placeholder={`Expected (${calibration.unit})`} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box', marginBottom: 6 }} />
              <p style={{ fontSize: 10, color: colors.textMuted, margin: '0 0 6px' }}>Draw a Length on a known dimension - it validates, does not save.</p>
              {calibrationTest && (
                <div style={{ background: '#FAFBFC', border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, fontSize: 11 }}>
                  <div>Expected: {formatNumber(calibrationTest.expected)} - Measured: {formatNumber(calibrationTest.measured)}</div>
                  <div style={{ fontWeight: 700, color: calibrationTest.accuracy >= 95 ? colors.accentGreen : colors.danger, marginTop: 4 }}>
                    Accuracy: {calibrationTest.accuracy.toFixed(2)}%
                  </div>
                  <button onClick={onClearTest} style={{ marginTop: 4, fontSize: 11, color: colors.textSecondary, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Clear</button>
                </div>
              )}
            </div>

            {calibrationHistory.length > 0 && <CalibrationHistoryPanel activeCalibration={calibration} historicalCalibrations={calibrationHistory} />}
          </>
        ) : (
          <div style={{ fontSize: 12, color: colors.warningText }}>Not calibrated - use the Calibrate tool on the canvas.</div>
        )}
      </SidebarSection>

      {/* Package 3+8 — Measurement Properties: context-aware section
          for whichever measurement is currently selected. Reuses
          existingMeasurements data already loaded (allMeasurements),
          computes nothing new — Value/Type read directly from the
          existing record, no duplicated calculation logic. */}
      {selectedMeasurementId && (() => {
        const m = allMeasurements.find((x) => x.id === selectedMeasurementId)
        if (!m) return null
        const isLocked = lockedIds.has(m.id)
        const currentOpacity = opacityMap.has(m.id) ? opacityMap.get(m.id) : 1
        return (
          <SidebarSection title="Measurement Properties">
            <SidebarRow label="ID" value={m.id.slice(0, 8)} />
            <SidebarRow label="Type" value={m.measurement_type} />
            <SidebarRow label="Layer" value={m.measurement_type} />
            <SidebarRow label="Polarity" value={m.polarity === 'negative' ? 'Negative' : 'Positive'} valueColor={m.polarity === 'negative' ? colors.danger : colors.accentGreen} />
            <SidebarRow label="Value" value={formatNumber(m.calculated_value)} bold />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
              <span style={{ fontSize: 12, color: colors.textSecondary }}>Locked</span>
              <button
                onClick={() => setLockedIds?.((prev) => { const next = new Set(prev); next.has(m.id) ? next.delete(m.id) : next.add(m.id); return next })}
                style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: `1px solid ${isLocked ? colors.primary : colors.border}`, background: isLocked ? '#EFF4FC' : '#fff', color: isLocked ? colors.primary : colors.textPrimary, cursor: 'pointer' }}
              >
                {isLocked ? '🔒 Locked' : '🔓 Unlocked'}
              </button>
            </div>
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: colors.textSecondary, marginBottom: 3 }}>
                <span>Opacity</span><span>{Math.round(currentOpacity * 100)}%</span>
              </div>
              {/* Package 3 — Opacity slider. Visual-only: writes to
                  opacityMap (measurementId -> number), never touches
                  geometry_data/calculated_value/aggregation. Range
                  10%-100% per the approved architecture, default
                  activation value 30% is applied the first time this
                  measurement gets an explicit non-100% value. */}
              <input
                type="range" min={10} max={100} value={Math.round(currentOpacity * 100)}
                onChange={(e) => setOpacityMap?.((prev) => { const next = new Map(prev); next.set(m.id, Number(e.target.value) / 100); return next })}
                style={{ width: '100%' }}
              />
            </div>
          </SidebarSection>
        )
      })()}

      <SidebarSection title="Active Takeoff Item">
        <Select value={selectedTakeoffItemId || ''} onChange={(e) => onSelectTakeoffItem(e.target.value)} style={{ width: '100%', fontSize: 13, marginBottom: 8 }}>
          {takeoffItems.length === 0 && <option value="">No items yet</option>}
          {takeoffItems.map((i) => <option key={i.id} value={i.id}>{i.description}</option>)}
        </Select>

        {!newItemOpen ? (
          <button onClick={() => setNewItemOpen(true)} style={{ fontSize: 11, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ New takeoff item</button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
            <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Description" style={{ fontSize: 12 }} />
            <div style={{ display: 'flex', gap: 4 }}>
              <Select value={newType} onChange={(e) => setNewType(e.target.value)} style={{ fontSize: 12, flex: 1 }}>
                {MEASUREMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
              <Input value={newUnit} onChange={(e) => setNewUnit(e.target.value)} placeholder="Unit" style={{ fontSize: 12, width: 60 }} />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Button size="sm" onClick={() => { if (newDesc && newUnit) { onNewTakeoffItem(newDesc, newUnit, newType); setNewDesc(''); setNewUnit(''); setNewItemOpen(false) } }}>Create</Button>
              <Button size="sm" variant="secondary" onClick={() => setNewItemOpen(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {selectedItem && (
          <>
            <SidebarRow label="Type" value={selectedItem.measurement_type} />
            <SidebarRow label="Unit" value={selectedItem.unit} />
            <SidebarRow label="Aggregated Qty" value={`${formatNumber(aggregatedQuantity)} ${selectedItem.unit}`} valueColor={colors.primary} bold />

            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${colors.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 6 }}>Capture into BOQ</div>
              <Select value={captureBoqId} onChange={(e) => setCaptureBoqId(e.target.value)} style={{ width: '100%', fontSize: 12, marginBottom: 6 }}>
                <option value="" disabled>Select BOQ item...</option>
                {boqItems.map((b) => <option key={b.id} value={b.id}>{b.item_no} - {b.description} (now: {formatNumber(b.resolved_quantity)})</option>)}
              </Select>
              <Button size="sm" disabled={!captureBoqId || aggregatedQuantity === undefined} onClick={() => onCapture(captureBoqId)} style={{ width: '100%' }}>
                Capture {formatNumber(aggregatedQuantity)} {selectedItem.unit}
              </Button>
            </div>
          </>
        )}
      </SidebarSection>

      <SidebarSection title="Layers">
        {['area', 'length', 'count'].map((layer) => (
          <label key={layer} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.textSecondary, padding: '3px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={!hiddenLayers.has(layer)} onChange={() => onToggleLayer(layer)} />
            {layer === 'area' ? 'Areas' : layer === 'length' ? 'Lengths' : 'Counts'}
          </label>
        ))}
      </SidebarSection>

      <CollapsibleSidebarSection title="Measurements" maxBodyHeight={320}>
        <Input
          value={measurementSearchQuery} onChange={(e) => setMeasurementSearchQuery(e.target.value)}
          placeholder="Search measurements..." style={{ width: '100%', fontSize: 12, marginBottom: 8 }}
        />
        {activeMeasurements.length === 0 && <div style={{ fontSize: 12, color: colors.textMuted }}>None yet.</div>}
        {activeMeasurements
          .filter((m) => !measurementSearchQuery || (displayIds[m.id] || '').toLowerCase().includes(measurementSearchQuery.toLowerCase()))
          .map((m) => {
          const isHidden = hiddenIds.has(m.id)
          const isSelected = selectedMeasurementId === m.id
          return (
            <div
              key={m.id}
              onClick={() => onSelectMeasurement(m.id)}
              style={{
                padding: '8px 10px', borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                background: isSelected ? '#FEF3C7' : '#FAFBFC', border: `1px solid ${isSelected ? '#F59E0B' : colors.border}`, opacity: isHidden ? 0.5 : 1,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: colors.primary }}>{displayIds[m.id]}</span>
                <button onClick={(e) => { e.stopPropagation(); onToggleHidden(m.id) }} title={isHidden ? 'Show' : 'Hide'} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>{isHidden ? 'Hidden' : 'Shown'}</button>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>{formatNumber(m.calculated_value)} {selectedItem?.unit}</div>
              <ReviewStatusBadge status={m.review_status} rejectionReason={m.rejection_reason} />
              {isSelected && (
                <button
                  onClick={(e) => { e.stopPropagation(); const v = window.prompt('Corrected value:', m.calculated_value); if (v && !isNaN(Number(v))) onSupersede(m.id, Number(v)) }}
                  style={{ marginTop: 4, fontSize: 11, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Supersede
                </button>
              )}
            </div>
          )
        })}

        {historyMeasurements.length > 0 && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${colors.border}` }}>
            <button onClick={() => setHistoryOpen((o) => !o)} style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              {historyOpen ? 'v' : '>'} History ({historyMeasurements.length} superseded)
            </button>
            {historyOpen && historyMeasurements.map((m) => (
              <div key={m.id} style={{ fontSize: 11, color: colors.textMuted, padding: '5px 0', borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ textDecoration: 'line-through' }}>{formatNumber(m.calculated_value)} {selectedItem?.unit}</div>
                <div>Reason: {m.superseded_reason || '-'} - {m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}</div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSidebarSection>

      {selectedMeasurementId && <ProvenancePanel measurementId={selectedMeasurementId} history={allMeasurements} unit={selectedItem?.unit} />}
    </div>
  )
}

// Compares the single ACTIVE calibration against past (inactive) ones on
// the same sheet. This does NOT imply multiple simultaneous active
// calibrations — the DB enforces exactly one active record per sheet via
// uq_active_calibration_per_sheet, and the active one is always the sole
// authority for quantity calculation. This panel only helps the estimator
// notice if repeated recalibrations produced meaningfully different
// scales over time (e.g. a mis-click), for review purposes.
function CalibrationHistoryPanel({ activeCalibration, historicalCalibrations }) {
  const [historyOpen, setHistoryOpen] = useState(false)
  const activeScale = activeCalibration.real_distance / activeCalibration.pixel_distance
  const allScales = [activeCalibration, ...historicalCalibrations].map((c) => c.real_distance / c.pixel_distance)
  const avgScale = allScales.reduce((a, b) => a + b, 0) / allScales.length
  const maxDeviationPct = Math.max(...allScales.map((s) => Math.abs(s - avgScale) / avgScale * 100))
  const isConsistent = maxDeviationPct < 2

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${colors.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 6 }}>
        Calibration History &amp; Cross-Validation
      </div>
      <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontWeight: 700, color: colors.primary }}>
        <span>Active: {formatNumber(activeCalibration.real_distance)} {activeCalibration.unit}</span>
        <span>{activeScale.toFixed(6)}</span>
      </div>

      <button onClick={() => setHistoryOpen((o) => !o)} style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}>
        {historyOpen ? 'v' : '>'} History ({historicalCalibrations.length} prior calibration{historicalCalibrations.length === 1 ? '' : 's'})
      </button>

      {historyOpen && historicalCalibrations.map((c) => {
        const scale = c.real_distance / c.pixel_distance
        const dev = Math.abs(scale - activeScale) / activeScale * 100
        return (
          <div key={c.id} style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: colors.textMuted, textDecoration: 'line-through' }}>
            <span>Prior: {formatNumber(c.real_distance)} {c.unit}</span>
            <span style={{ color: dev > 2 ? colors.danger : colors.textMuted, textDecoration: 'none' }}>{dev.toFixed(2)}% dev</span>
          </div>
        )
      })}
      <div style={{ marginTop: 6, padding: '5px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: isConsistent ? colors.successBg : colors.dangerBg, color: isConsistent ? colors.successText : colors.danger }}>
        {isConsistent ? 'Historically Consistent' : `Warning - ${maxDeviationPct.toFixed(2)}% deviation from history`}
      </div>
      <p style={{ fontSize: 10, color: colors.textMuted, marginTop: 4 }}>
        Only the active calibration is used for quantity calculation. History is shown for review only.
      </p>
    </div>
  )
}

function ProvenancePanel({ measurementId, history, unit }) {
  function findLineage(id) {
    const byId = Object.fromEntries(history.map((m) => [m.id, m]))
    let rootId = id, guard = 0
    while (guard++ < 50) {
      const predecessor = history.find((m) => m.superseded_by === rootId)
      if (!predecessor) break
      rootId = predecessor.id
    }
    const chain = []
    let ptr = byId[rootId]
    guard = 0
    while (ptr && guard++ < 50) { chain.push(ptr); ptr = ptr.superseded_by ? byId[ptr.superseded_by] : null }
    return chain
  }
  const chain = findLineage(measurementId)
  return (
    <CollapsibleSidebarSection title="Provenance / Lineage" defaultOpen={false} maxBodyHeight={240}>
      {chain.map((m, i) => (
        <div key={m.id} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          <div style={{ width: 12, fontSize: 11, color: colors.textMuted }}>{i === 0 ? '*' : 'v'}</div>
          <div style={{ flex: 1, fontSize: 11, opacity: m.id === measurementId ? 1 : 0.6 }}>
            <div style={{ fontWeight: m.id === measurementId ? 700 : 500, color: m.is_active ? colors.textPrimary : colors.textMuted, textDecoration: m.is_active ? 'none' : 'line-through' }}>{formatNumber(m.calculated_value)} {unit}</div>
            <div style={{ color: colors.textMuted }}>{m.source_type === 'ai_detected' ? 'AI-detected' : 'Manual'} - {m.created_at ? new Date(m.created_at).toLocaleString() : '-'}</div>
            {m.superseded_reason && <div style={{ color: colors.warningText }}>Superseded: {m.superseded_reason}</div>}
          </div>
        </div>
      ))}
      {chain.length <= 1 && <div style={{ fontSize: 11, color: colors.textMuted }}>Original measurement, no corrections.</div>}
    </CollapsibleSidebarSection>
  )
}

// Package 4 UI — pure presentational badge, no state, no calculation.
// Mirrors the review_status/rejection_reason columns already loaded by
// loadMeasurements() verbatim.
function ReviewStatusBadge({ status, rejectionReason }) {
  const config = {
    unreviewed: { label: 'Unreviewed', bg: '#F1F5F9', color: '#64748B' },
    pending: { label: 'Pending', bg: '#FEF3C7', color: '#92400E' },
    accepted: { label: 'Accepted', bg: '#D1FAE5', color: '#065F46' },
    rejected: { label: 'Rejected', bg: '#FEE2E2', color: '#991B1B' },
  }[status] || { label: status || 'Unreviewed', bg: '#F1F5F9', color: '#64748B' }
  return (
    <div style={{ marginTop: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999, background: config.bg, color: config.color }}>
        {config.label}
      </span>
      {status === 'rejected' && rejectionReason && (
        <div style={{ fontSize: 10, color: '#991B1B', marginTop: 2 }}>{rejectionReason}</div>
      )}
    </div>
  )
}

function SidebarSection({ title, children }) {
  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  )
}
// UX refinement — collapsible variant for potentially long sections
// (Drawings/Sheets, Measurements list, Calibration History). Reuses
// the exact same visual shell as SidebarSection; only adds a
// disclosure header (˅/˄) and an internally-scrollable body so a long
// list never forces the whole Right Sidebar to grow indefinitely.
function CollapsibleSidebarSection({ title, defaultOpen = true, maxBodyHeight = 260, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 12 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginBottom: open ? 8 : 0 }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</span>
        <span style={{ fontSize: 11, color: colors.textMuted }}>{open ? '˄' : '˅'}</span>
      </button>
      {open && (
        <div style={{ maxHeight: maxBodyHeight, overflowY: 'auto' }}>
          {children}
        </div>
      )}
    </div>
  )
}
function SidebarRow({ label, value, valueColor, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
      <span style={{ color: colors.textMuted }}>{label}</span>
      <span style={{ color: valueColor || colors.textPrimary, fontWeight: bold ? 700 : 500 }}>{value}</span>
    </div>
  )
}
